import React, { ReactNode } from 'react';
import { Box, Card, CardContent, Typography, Divider, SxProps, Theme } from '@mui/material';

export interface MobileCardField<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  getValue?: (row: T) => any;
  hideLabel?: boolean;
  primary?: boolean;
}

interface MobileCardViewProps<T> {
  data: T[];
  fields: MobileCardField<T>[];
  onRowClick?: (row: T) => void;
  keyExtractor: (row: T) => string | number;
  cardSx?: (row: T) => SxProps<Theme>;
}

export default function MobileCardView<T>({ data, fields, onRowClick, keyExtractor, cardSx }: MobileCardViewProps<T>) {
  const primaryField = fields.find(f => f.primary);
  const otherFields = fields.filter(f => !f.primary);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0 }}>
      {data.map((row) => {
        const cardContent = (
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* プライマリフィールド（カードタイトル） */}
            {primaryField && (
              <Box sx={{ mb: 1.5 }}>
                {primaryField.render ? (
                  primaryField.render(row)
                ) : (
                  <Typography variant="subtitle1" fontWeight={600}>
                    {primaryField.getValue ? primaryField.getValue(row) : (row as any)[primaryField.key]}
                  </Typography>
                )}
              </Box>
            )}

            {/* その他のフィールド */}
            {otherFields.map((field, index) => (
              <React.Fragment key={field.key}>
                {field.hideLabel ? (
                  <Box sx={{ mt: 1 }}>
                    {field.render ? field.render(row) : (
                      <Typography variant="body2">
                        {field.getValue ? field.getValue(row) : (row as any)[field.key]}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: '2px',
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 2, flexShrink: 0, fontSize: '0.75rem' }}>
                      {field.label}
                    </Typography>
                    <Box sx={{ textAlign: 'right', overflow: 'hidden' }}>
                      {field.render ? field.render(row) : (
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          {field.getValue ? field.getValue(row) : (row as any)[field.key]}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {!field.hideLabel && index < otherFields.length - 1 && (
                  <Divider sx={{ my: 0 }} />
                )}
              </React.Fragment>
            ))}
          </CardContent>
        );

        return (
          <Card
            key={keyExtractor(row)}
            variant="outlined"
            sx={{
              borderRadius: 2,
              backgroundColor: '#fff',
              ...(onRowClick && {
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }),
              ...(cardSx ? cardSx(row) as object : {}),
            }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {cardContent}
          </Card>
        );
      })}
      {data.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            データがありません
          </Typography>
        </Box>
      )}
    </Box>
  );
}
